import React, { useState, useMemo } from 'react';
import './ChapterView.css';

const ChapterView = ({ chapter, onBack, bookTitle, promptContext = {}, actionsData = null, quizData = null, expertsData = null }) => {
  const [activeTab, setActiveTab] = useState('overview');

  // Memoized helper functions to get chapter-specific data from supplementary files
  // Falls back to chapter data if supplementary file doesn't have data for this chapter
  const chapterActions = useMemo(() => {
    if (actionsData?.chapters) {
      const chapterData = actionsData.chapters.find(c => c.chapterNumber === chapter.ChapterNumber);
      if (chapterData?.Actions && chapterData.Actions.length > 0) {
        return chapterData.Actions;
      }
    }
    return chapter.Actions || [];
  }, [actionsData, chapter.ChapterNumber, chapter.Actions]);

  const chapterQuiz = useMemo(() => {
    if (quizData?.chapters) {
      const chapterData = quizData.chapters.find(c => c.chapterNumber === chapter.ChapterNumber);
      if (chapterData?.quiz && chapterData.quiz.length > 0) {
        return chapterData.quiz;
      }
    }
    return chapter.Quiz || [];
  }, [quizData, chapter.ChapterNumber, chapter.Quiz]);

  const chapterExperts = useMemo(() => {
    if (expertsData?.chapters) {
      const chapterData = expertsData.chapters.find(c => c.chapterNumber === chapter.ChapterNumber);
      if (chapterData?.Experts && chapterData.Experts.length > 0) {
        return chapterData.Experts;
      }
    }
    return chapter.Experts || [];
  }, [expertsData, chapter.ChapterNumber, chapter.Experts]);

  const tabs = [
    { id: 'overview', label: '📋 Overview' },
    { id: 'ideamap', label: '🗺️ Ideamap' },
    { id: 'takeaways', label: '💡 Key Takeaways' },
    { id: 'quiz', label: '❓ Quiz' },
    { id: 'actions', label: '✅ Actions' },
    { id: 'experts', label: '👥 Experts' }
  ];

  // Build full prompt combining context + chapter prompt
  // Order: BookTitle (if checked) + Persona + BookMetaPrompt + Prompt + Keywords
  const buildFullPrompt = (chapterPrompt) => {
    const parts = [];
    if (promptContext.includeBookTitle && bookTitle?.trim()) {
      parts.push(`As discussed in "${bookTitle.trim()}",`);
    }
    if (promptContext.persona?.trim()) {
      parts.push(`As a ${promptContext.persona.trim()},`);
    }
    if (promptContext.metaPrompt?.trim()) {
      parts.push(`Context: ${promptContext.metaPrompt.trim()}`);
    }
    parts.push(chapterPrompt);
    if (promptContext.keywords?.trim()) {
      parts.push(`Focus on these keywords: ${promptContext.keywords.trim()}`);
    }
    return parts.join(' ');
  };

  const renderOverview = () => (
    <div className="tab-content-inner">
      <div className="overview-text">
        <p>{chapter.Overview}</p>
      </div>

      {chapter.PracticalApplication && (
        <div className="application-section">
          <h4>🎯 Practical Application</h4>
          <p>{chapter.PracticalApplication}</p>
        </div>
      )}

      {chapter.ChapterPrompts && chapter.ChapterPrompts.length > 0 && (
        <div className="prompts-section">
          <h4>💭 Discussion Prompts</h4>
          <table className="prompts-table">
            <tbody>
              {chapter.ChapterPrompts.map((prompt, idx) => {
                const fullPrompt = buildFullPrompt(prompt);
                const encodedPrompt = encodeURIComponent(fullPrompt);
                return (
                  <tr key={idx}>
                    <td className="prompt-text">{prompt}</td>
                    <td className="prompt-links">
                      <a 
                        href={`https://chat.openai.com/?q=${encodedPrompt}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Ask in ChatGPT (with context)"
                        className="llm-link"
                      >
                        <img src="https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg" alt="ChatGPT" className="llm-icon" />
                      </a>
                      <a 
                        href={`https://grok.com/?q=${encodedPrompt}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Ask in Grok (with context)"
                        className="llm-link"
                      >
                        <span className="llm-icon" style={{ fontSize: '18px', fontWeight: 'bold' }}>𝕏</span>
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {chapter.NextSteps && (
        <div className="next-steps-section">
          <h4>➡️ Next Steps</h4>
          <p>{chapter.NextSteps}</p>
        </div>
      )}
    </div>
  );

  const renderIdeamap = () => (
    <div className="tab-content-inner">
      {chapter.IdeamapUrl ? (
        <div className="ideamap-section">
          <div className="ideamap-container">
            <img 
              src={chapter.IdeamapUrl} 
              alt={`Idea map for ${chapter.Title}`} 
              className="ideamap-image-large"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="ideamap-placeholder" style={{ display: 'none' }}>
              <span>🗺️</span>
              <p>Ideamap not available</p>
            </div>
          </div>
        </div>
      ) : (
        <p className="empty-message">No ideamap available for this chapter.</p>
      )}
    </div>
  );

  const renderTakeaways = () => (
    <div className="tab-content-inner">
      {chapter.KeyTakeaways && chapter.KeyTakeaways.length > 0 ? (
        <div className="takeaways-list">
          {chapter.KeyTakeaways.map((item, idx) => (
            <div key={idx} className="takeaway-item">
              <span className="takeaway-number">{item.point}</span>
              <div 
                className="takeaway-text"
                dangerouslySetInnerHTML={{ 
                  __html: item.takeaway.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                }}
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="empty-message">No key takeaways available for this chapter.</p>
      )}
    </div>
  );

  const renderQuiz = () => (
    <div className="tab-content-inner">
      {chapterQuiz && chapterQuiz.length > 0 ? (
        <div className="quiz-container">
          {chapterQuiz.map((q, idx) => (
            <QuizQuestion key={idx} question={q} />
          ))}
        </div>
      ) : (
        <p className="empty-message">No quiz available for this chapter.</p>
      )}
    </div>
  );

  const renderActions = () => (
    <div className="tab-content-inner">
      {chapterActions && chapterActions.length > 0 ? (
        <div className="actions-list">
          {chapterActions.map((action, idx) => (
            <div key={idx} className="action-item">
              <div className="action-header">
                <span className="action-number">{idx + 1}</span>
                <span className="action-timeframe">⏱️ {action.timeframe}</span>
              </div>
              <div className="action-content">
                <p className="action-text">{action.action}</p>
                <p className="action-outcome">
                  <strong>Expected Outcome:</strong> {action.expectedOutcome}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="empty-message">No actions available for this chapter.</p>
      )}
    </div>
  );

  const renderExperts = () => (
    <div className="tab-content-inner">
      {chapterExperts && chapterExperts.length > 0 ? (
        <div className="experts-list">
          {chapterExperts.map((expert, idx) => (
            <div key={idx} className="expert-card">
              <div className="expert-header">
                <h4 className="expert-name">{expert.name}</h4>
              </div>
              <p className="expert-expertise">{expert.expertise}</p>
              
              {expert.resources && expert.resources.length > 0 && (
                <div className="expert-resources">
                  <h5>Resources:</h5>
                  <div className="resources-list">
                    {expert.resources.map((resource, rIdx) => (
                      <a 
                        key={rIdx}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`resource-link resource-${resource.type}`}
                      >
                        <span className="resource-icon">
                          {resource.type === 'book' && '📚'}
                          {resource.type === 'article' && '📄'}
                          {resource.type === 'video' && '🎬'}
                          {resource.type === 'podcast' && '🎙️'}
                          {resource.type === 'course' && '🎓'}
                        </span>
                        <span className="resource-title">{resource.title}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="empty-message">No experts available for this chapter.</p>
      )}
    </div>
  );

  return (
    <div className="chapter-view">
      <div className="chapter-view-header">
        <div className="chapter-breadcrumb">
          <span className="breadcrumb-book">{bookTitle}</span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-chapter">Chapter {chapter.ChapterNumber}</span>
        </div>
      </div>

      <div className="chapter-title-section">
        <span className="chapter-badge">Chapter {chapter.ChapterNumber}</span>
        <h2 className="chapter-view-title">{chapter.Title}</h2>
      </div>

      <div className="chapter-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`chapter-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="chapter-tab-content">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'ideamap' && renderIdeamap()}
        {activeTab === 'takeaways' && renderTakeaways()}
        {activeTab === 'quiz' && renderQuiz()}
        {activeTab === 'actions' && renderActions()}
        {activeTab === 'experts' && renderExperts()}
      </div>
    </div>
  );
};

// Quiz Question Component with answer reveal functionality
const QuizQuestion = ({ question }) => {
  const [showAnswer, setShowAnswer] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  const handleOptionSelect = (optionKey) => {
    setSelectedOption(optionKey);
    setShowAnswer(true);
  };

  const isCorrect = selectedOption === question.correctAnswer;

  return (
    <div className="quiz-question">
      <div className="question-header">
        <span className="question-number">Q{question.questionNumber}</span>
        <p className="question-text">{question.question}</p>
      </div>

      <div className="options-list">
        {Object.entries(question.options).map(([key, value]) => (
          <button
            key={key}
            className={`option-btn ${
              selectedOption === key 
                ? (key === question.correctAnswer ? 'correct' : 'incorrect')
                : ''
            } ${showAnswer && key === question.correctAnswer ? 'correct' : ''}`}
            onClick={() => handleOptionSelect(key)}
            disabled={showAnswer}
          >
            <span className="option-key">{key}</span>
            <span className="option-value">{value}</span>
          </button>
        ))}
      </div>

      {showAnswer && (
        <div className={`feedback ${isCorrect ? 'correct' : 'incorrect'}`}>
          <span className="feedback-icon">{isCorrect ? '✅' : '❌'}</span>
          <span className="feedback-text">{question.feedback}</span>
        </div>
      )}
    </div>
  );
};

export default ChapterView;
